import { datum, datumIzMs } from '../oblika/oblika';

/** Čas iz epoch ms za prikaz: »10. 10. 2026 09:05«. */
export function cas(ms?: number | null): string {
  if (!ms) {
    return '';
  }
  const d = new Date(ms);
  return `${datum(datumIzMs(ms))} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Predlog domene iz naziva podjetja: »Avtohiša Novak d.o.o.« → »avtohisa-novak« (končno preveri strežnik). */
export function predlogDomene(naziv: string): string {
  return naziv
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s*\b(d\.?\s?o\.?\s?o|s\.?\s?p|d\.?\s?d)\.?\s*$/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/** Stolpci seznama instanc, po katerih se da razvrstiti. */
export type RazvrscanjeInstanc =
  | 'naziv'
  | 'domena'
  | 'aktivna'
  | 'uporabnikov'
  | 'racunov'
  | 'zadnjiRacun'
  | 'zadnjaPrijava'
  | 'verzija'
  | 'aktivnihDni'
  | 'narocnina'
  | 'placanoDo'
  | 'tocke';

/**
 * Seznam instanc razvrščen po stolpcu (cel seznam je naložen, zato na odjemalcu). Prazne vrednosti so vedno na koncu;
 * aktivne instance so pri naraščajočem razvrščanju prve, verzija (»2.4«) se primerja kot število. Brez {@code razvrsti} ostane vrstni red strežnika (po nazivu).
 */
export function razvrstiInstance<T extends Partial<Record<RazvrscanjeInstanc, string | number | boolean | null>>>(
  instance: T[],
  razvrsti?: RazvrscanjeInstanc,
  smer: 'asc' | 'desc' = 'asc',
): T[] {
  if (!razvrsti) {
    return instance;
  }
  const vrednost = (i: T) => {
    const v = i[razvrsti];
    if (razvrsti === 'aktivna') {
      return v ? 0 : 1;
    }
    return razvrsti === 'verzija' && v ? Number(v) : v;
  };
  const predznak = smer === 'desc' ? -1 : 1;
  return [...instance].sort((a, b) => {
    const va = vrednost(a);
    const vb = vrednost(b);
    if (va === null || va === undefined || va === '') {
      return vb === null || vb === undefined || vb === '' ? 0 : 1;
    }
    if (vb === null || vb === undefined || vb === '') {
      return -1;
    }
    const r =
      typeof va === 'string' && typeof vb === 'string'
        ? va.localeCompare(vb, 'sl', { sensitivity: 'base' })
        : Number(va) - Number(vb);
    return r * predznak;
  });
}

/** Zadnjih {@code n} dni do vključno {@code danes} kot ISO datumi (najstarejši prvi), za pas aktivnosti. */
export function zadnjihDni(n: number, danes: Date = new Date()): string[] {
  const dnevi: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(danes.getFullYear(), danes.getMonth(), danes.getDate() - i);
    dnevi.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
    );
  }
  return dnevi;
}

/** Jakost dneva v pasu aktivnosti: 0 = brez, 1 = malo (do 20 zahtev), 2 = srednje (do 100), 3 = veliko. */
export function jakost(zahtev: number): 0 | 1 | 2 | 3 {
  if (zahtev <= 0) {
    return 0;
  }
  return zahtev <= 20 ? 1 : zahtev <= 100 ? 2 : 3;
}

/** Prvi in zadnji dan meseca datuma {@code danes} kot ISO (privzeto obdobje seznama nakazil). */
export function mesec(danes: Date = new Date()): [string, string] {
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return [
    iso(new Date(danes.getFullYear(), danes.getMonth(), 1)),
    iso(new Date(danes.getFullYear(), danes.getMonth() + 1, 0)),
  ];
}
